import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useCircuitEditor } from '../hooks/useCircuitEditor';
import { X, ChevronLeft, ChevronRight, Download, FileCode, Cpu, Eye, Code, Maximize2, Minimize2, List, Zap } from 'lucide-react';

/**
 * Extract BOM (Bill of Materials) from tscircuit TSX source files.
 * Parses component tags and extracts name, type, value, footprint.
 */
function extractBOM(circuitFiles) {
  if (!circuitFiles) return [];

  const componentTypes = [
    { tag: 'resistor', valueAttr: 'resistance' },
    { tag: 'capacitor', valueAttr: 'capacitance' },
    { tag: 'inductor', valueAttr: 'inductance' },
    { tag: 'diode', valueAttr: null },
    { tag: 'led', valueAttr: 'color' },
    { tag: 'chip', valueAttr: null },
    { tag: 'mosfet', valueAttr: null },
    { tag: 'transistor', valueAttr: null },
    { tag: 'pinheader', valueAttr: 'pinCount' },
    { tag: 'crystal', valueAttr: 'frequency' },
    { tag: 'fuse', valueAttr: 'rating' },
  ];

  const components = [];
  const allCode = circuitFiles.map(f => f.code).join('\n');

  for (const { tag, valueAttr } of componentTypes) {
    // Match self-closing and open tags: <resistor ... /> or <resistor ...>
    const tagRegex = new RegExp(`<${tag}\\b([^>]*?)\\s*/?>`, 'gi');
    let match;
    while ((match = tagRegex.exec(allCode)) !== null) {
      const attrs = match[1];
      const getName = (s) => {
        const m = s.match(/\bname\s*=\s*["']([^"']+)["']/);
        return m ? m[1] : '';
      };
      const getAttr = (s, attr) => {
        if (!attr) return '';
        const m = s.match(new RegExp(`\\b${attr}\\s*=\\s*["']([^"']+)["']`));
        // Also try JSX expression: attr={value} or attr="value"
        if (m) return m[1];
        const m2 = s.match(new RegExp(`\\b${attr}\\s*=\\s*\\{([^}]+)\\}`));
        return m2 ? m2[1].replace(/["']/g, '').trim() : '';
      };
      const getFootprint = (s) => {
        const m = s.match(/\bfootprint\s*=\s*["']([^"']+)["']/);
        if (m) return m[1];
        const m2 = s.match(/\bfootprint\s*=\s*\{([^}]+)\}/);
        return m2 ? m2[1].replace(/["'`]/g, '').replace(/FP\./g, '').trim() : '';
      };

      components.push({
        ref: getName(attrs),
        type: tag.charAt(0).toUpperCase() + tag.slice(1),
        value: getAttr(attrs, valueAttr),
        footprint: getFootprint(attrs),
      });
    }
  }

  // Sort by reference designator
  components.sort((a, b) => {
    const prefA = a.ref.replace(/\d+/g, '');
    const prefB = b.ref.replace(/\d+/g, '');
    if (prefA !== prefB) return prefA.localeCompare(prefB);
    const numA = parseInt(a.ref.replace(/\D+/g, '')) || 0;
    const numB = parseInt(b.ref.replace(/\D+/g, '')) || 0;
    return numA - numB;
  });

  return components;
}

/**
 * Group BOM components by type+value+footprint for summary view.
 */
function groupBOM(components) {
  const groups = {};
  for (const c of components) {
    const key = `${c.type}|${c.value}|${c.footprint}`;
    if (!groups[key]) {
      groups[key] = { type: c.type, value: c.value, footprint: c.footprint, qty: 0, refs: [] };
    }
    groups[key].qty++;
    if (c.ref) groups[key].refs.push(c.ref);
  }
  return Object.values(groups).sort((a, b) => a.type.localeCompare(b.type) || a.value.localeCompare(b.value));
}

/**
 * CircuitEditorPanel - Circuit design viewer/editor panel
 *
 * Desktop: right-side resizable panel (same pattern as DiagramEditorPanel)
 * Touch/Mobile: fullscreen overlay
 *
 * Displays tscircuit TSX source files with download capability.
 * Visual tab renders live schematic/PCB via tscircuit standalone bundle in iframe.
 */

const MIN_WIDTH = 400;
const DEFAULT_WIDTH = 600;
const STORAGE_KEY = 'circuitEditorPanelWidth';

const CircuitEditorPanel = () => {
  const { isOpen, circuitFiles, circuitTitle, close } = useCircuitEditor();

  const getSavedWidth = () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  };

  const [width, setWidth] = useState(getSavedWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const [activeFile, setActiveFile] = useState(0);
  const [viewMode, setViewMode] = useState('visual'); // 'visual', 'code', or 'bom'
  const [isMaximized, setIsMaximized] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  // Iframe-side compile/render errors surfaced from tscircuit runframe.
  // Without this, AI-generated TSX with a compile bug (missing import,
  // unresolvable design_system reference, wrong tscircuit API) shows an
  // empty visual pane with no explanation — looks like AI failure when
  // it's actually a recoverable iframe-side issue.
  const [iframeError, setIframeError] = useState(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Detect touch devices
  useEffect(() => {
    const checkTouch = () => {
      const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const small = window.innerWidth < 768;
      setIsTouch(touch || small);
    };
    checkTouch();
    window.addEventListener('resize', checkTouch);
    return () => window.removeEventListener('resize', checkTouch);
  }, []);

  // Open/close animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true));
      });
      setActiveFile(0);
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Save width
  useEffect(() => {
    if (width !== DEFAULT_WIDTH) {
      localStorage.setItem(STORAGE_KEY, String(width));
    }
  }, [width]);

  // Drag to resize
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
  }, [width]);

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      const delta = startXRef.current - e.clientX;
      const newWidth = Math.max(MIN_WIDTH, Math.min(window.innerWidth * 0.8, startWidthRef.current + delta));
      setWidth(newWidth);
    };
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Download a single file
  const downloadFile = useCallback((file) => {
    const blob = new Blob([file.code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.file_name.split('/').pop();
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  // Download all files as individual downloads
  const downloadAll = useCallback(() => {
    if (!circuitFiles) return;
    circuitFiles.forEach(file => downloadFile(file));
  }, [circuitFiles, downloadFile]);

  // Run SPICE simulation: inject analogsimulation + voltageprobes into TSX, resend to iframe
  const runSimulation = useCallback(() => {
    if (!circuitFiles || !iframeRef.current) return;
    setIsSimulating(true);
    setViewMode('visual');

    // Build fsMap with simulation components injected into main.tsx
    const fsMap = {};
    circuitFiles.forEach(f => {
      let code = f.code;
      if (f.file_name.endsWith('main.tsx') || (circuitFiles.length === 1)) {
        // Skip if simulation already present
        if (!code.includes('analogsimulation') && !code.includes('AnalogSimulation')) {
          // Inject voltageprobes on named nets and analogsimulation before closing </board>
          const simBlock = [
            '',
            '  {/* Auto-injected SPICE simulation */}',
            '  <analogsimulation duration="10ms" timePerStep="0.01ms" spiceEngine="ngspice" />',
          ].join('\n');

          // Insert before </board>
          const boardCloseIdx = code.lastIndexOf('</board>');
          if (boardCloseIdx !== -1) {
            code = code.slice(0, boardCloseIdx) + simBlock + '\n' + code.slice(boardCloseIdx);
          }
        }
      }
      fsMap[f.file_name] = code;
    });

    const mainFile = circuitFiles.find(f => f.file_name.endsWith('main.tsx'))
      || circuitFiles[circuitFiles.length - 1];

    // Reset iframe and resend with simulation
    sentRef.current = false;
    const handleReady = (e) => {
      if (e.data?.runframe_type === 'runframe_ready_to_receive' && iframeRef.current && !sentRef.current) {
        sentRef.current = true;
        iframeRef.current.contentWindow.postMessage({
          runframe_type: 'runframe_props_changed',
          runframe_props: { fsMap, entrypoint: mainFile.file_name }
        }, '*');
        setIsSimulating(false);
        window.removeEventListener('message', handleReady);
      }
    };
    window.addEventListener('message', handleReady);

    // Force iframe reload to trigger fresh compile with simulation
    if (iframeRef.current) {
      iframeRef.current.src = iframeRef.current.src;
    }

    // Timeout fallback
    setTimeout(() => {
      setIsSimulating(false);
      window.removeEventListener('message', handleReady);
    }, 15000);
  }, [circuitFiles]);

  // tscircuit hosted iframe + postMessage communication
  const iframeRef = useRef(null);
  const sentRef = useRef(false);

  // Reset error state when files change so retry on a new circuit clears
  // the prior overlay.
  useEffect(() => { setIframeError(null); }, [circuitFiles]);

  useEffect(() => {
    if (viewMode !== 'visual' || !circuitFiles) return;
    sentRef.current = false;
    const extractErrorMessage = (data) => {
      const e = data?.error ?? data?.runframe_error ?? data?.errors;
      if (!e) return null;
      if (typeof e === 'string') return e;
      if (Array.isArray(e)) {
        return e.map(it => (typeof it === 'string' ? it : (it?.message || JSON.stringify(it)))).join('\n');
      }
      if (typeof e === 'object') return e.message || e.text || JSON.stringify(e);
      return String(e);
    };
    const handleMessage = (e) => {
      const data = e.data;
      if (!data || typeof data !== 'object') return;
      // Hand-off: post the user's circuit files when iframe is ready.
      if (data.runframe_type === 'runframe_ready_to_receive' && iframeRef.current && !sentRef.current) {
        sentRef.current = true;
        const fsMap = {};
        circuitFiles.forEach(f => { fsMap[f.file_name] = f.code; });
        const mainFile = circuitFiles.find(f => f.file_name.endsWith('main.tsx'))
          || circuitFiles[circuitFiles.length - 1];
        iframeRef.current.contentWindow.postMessage({
          runframe_type: 'runframe_props_changed',
          runframe_props: { fsMap, entrypoint: mainFile.file_name }
        }, '*');
        return;
      }
      // Defensive error capture — tscircuit's runframe may emit error frames
      // under several shapes depending on bundle version. Cover the known
      // ones: explicit `runframe_error` type, render_error type, or any
      // payload with an `error` / `errors` field. Without this catch-all,
      // a compile failure inside the iframe is silent to the user.
      const errType = data.runframe_type;
      if (
        errType === 'runframe_error' ||
        errType === 'render_error' ||
        errType === 'compile_error' ||
        data.error || data.errors || data.runframe_error
      ) {
        const msg = extractErrorMessage(data);
        if (msg) {
          console.error('[circuit-iframe]', data);
          setIframeError(msg);
          sentRef.current = false; // permit retry on next ready event
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [viewMode, circuitFiles]);

  // Force iframe reload to retry compilation. Used by the error-overlay
  // retry button when the user wants another shot after fixing TSX or
  // when the failure was transient.
  const retryIframe = () => {
    setIframeError(null);
    sentRef.current = false;
    if (iframeRef.current) {
      const src = iframeRef.current.src;
      iframeRef.current.src = src;
    }
  };

  const bomItems = useMemo(() => extractBOM(circuitFiles), [circuitFiles]);
  const bomGrouped = useMemo(() => groupBOM(bomItems), [bomItems]);

  if (!shouldRender || !circuitFiles) return null;

  const currentFile = circuitFiles[activeFile] || circuitFiles[0];

  return (
    <div
      className={`circuit-editor-panel ${isTouch ? 'circuit-editor-panel--mobile' : 'circuit-editor-panel--desktop'}`}
      style={isMaximized ? {
        position: 'fixed',
        top: '5vh',
        left: '5vw',
        right: '5vw',
        bottom: '5vh',
        width: 'auto',
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--bg-primary, #1a1a1a)',
        border: '1px solid rgba(128,128,128,0.3)',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.3s ease',
      } : {
        position: 'fixed',
        top: isTouch ? 0 : '56px',
        right: 0,
        bottom: 0,
        width: isTouch ? '100%' : `${width}px`,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--bg-primary, #1a1a1a)',
        borderLeft: isTouch ? 'none' : '1px solid rgba(128,128,128,0.2)',
        transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        transition: isDragging ? 'none' : 'transform 0.3s ease',
      }}
    >
      {/* Drag handle (desktop side panel only) */}
      {!isTouch && !isMaximized && (
        <div
          onMouseDown={handleMouseDown}
          style={{
            position: 'absolute',
            left: -3,
            top: 0,
            bottom: 0,
            width: 6,
            cursor: 'col-resize',
            zIndex: 10,
          }}
        />
      )}

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        borderBottom: '1px solid rgba(128,128,128,0.2)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Cpu size={16} />
          <span style={{ fontWeight: 600, fontSize: '14px' }}>{circuitTitle}</span>
          <span style={{ fontSize: '11px', opacity: 0.6 }}>{circuitFiles.length} files</span>
        </div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {/* Visual / Code toggle */}
          <div style={{
            display: 'flex',
            borderRadius: '6px',
            border: '1px solid rgba(128,128,128,0.3)',
            overflow: 'hidden',
          }}>
            <button onClick={() => setViewMode('visual')} title="Visual view" style={{
              background: viewMode === 'visual' ? 'rgba(59,130,246,0.2)' : 'none',
              border: 'none', cursor: 'pointer', padding: '4px 8px',
              color: viewMode === 'visual' ? '#3b82f6' : 'inherit',
              opacity: viewMode === 'visual' ? 1 : 0.5,
              display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px',
            }}>
              <Eye size={13} /> Visual
            </button>
            <button onClick={() => setViewMode('code')} title="Code view" style={{
              background: viewMode === 'code' ? 'rgba(59,130,246,0.2)' : 'none',
              border: 'none', cursor: 'pointer', padding: '4px 8px',
              color: viewMode === 'code' ? '#3b82f6' : 'inherit',
              opacity: viewMode === 'code' ? 1 : 0.5,
              display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px',
            }}>
              <Code size={13} /> Code
            </button>
            <button onClick={() => setViewMode('bom')} title="Bill of Materials" style={{
              background: viewMode === 'bom' ? 'rgba(59,130,246,0.2)' : 'none',
              border: 'none', cursor: 'pointer', padding: '4px 8px',
              color: viewMode === 'bom' ? '#3b82f6' : 'inherit',
              opacity: viewMode === 'bom' ? 1 : 0.5,
              display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px',
            }}>
              <List size={13} /> BOM
            </button>
          </div>
          <button
            onClick={runSimulation}
            disabled={isSimulating}
            title="Run SPICE simulation"
            style={{
              background: isSimulating ? 'rgba(234,179,8,0.2)' : 'rgba(34,197,94,0.15)',
              border: '1px solid rgba(34,197,94,0.3)',
              cursor: isSimulating ? 'wait' : 'pointer',
              padding: '4px 10px',
              color: isSimulating ? '#eab308' : '#22c55e',
              borderRadius: '6px',
              display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px',
              fontWeight: 600,
            }}
          >
            <Zap size={13} />
            {isSimulating ? 'Simulating...' : 'Simulate'}
          </button>
          <button onClick={downloadAll} title="Download all files" style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
            color: 'inherit', opacity: 0.7, borderRadius: '4px',
          }}>
            <Download size={16} />
          </button>
          {isMaximized ? (
            <button onClick={() => setIsMaximized(false)} title="Minimize" style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
              color: 'inherit', opacity: 0.7, borderRadius: '4px',
            }}>
              <Minimize2 size={16} />
            </button>
          ) : (
            <button onClick={() => setIsMaximized(true)} title="Maximize" style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
              color: 'inherit', opacity: 0.7, borderRadius: '4px',
            }}>
              <Maximize2 size={16} />
            </button>
          )}
          <button onClick={close} title="Close" style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
            color: 'inherit', opacity: 0.7, borderRadius: '4px',
          }}>
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Visual mode: tscircuit hosted iframe */}
      {viewMode === 'visual' && (
        <div style={{ flex: 1, overflow: 'hidden', background: '#fff', position: 'relative' }}>
          <iframe
            ref={iframeRef}
            src="/circuit-iframe.html"
            title="Circuit Preview"
            sandbox="allow-scripts allow-same-origin"
            style={{ width: '100%', height: '100%', border: 'none', flex: 1 }}
          />
          {iframeError && (
            <div
              style={{
                position: 'absolute', inset: 0,
                background: 'rgba(20,0,0,0.92)', color: '#fff',
                padding: '20px',
                display: 'flex', flexDirection: 'column', gap: '12px',
                overflow: 'auto', fontFamily: 'monospace', fontSize: '12px',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '14px', color: '#ff8888' }}>
                Circuit failed to render
              </div>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                {iframeError}
              </pre>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button
                  onClick={retryIframe}
                  style={{
                    padding: '6px 14px', borderRadius: '6px',
                    background: '#3b82f6', color: '#fff', border: 'none',
                    cursor: 'pointer', fontSize: '12px',
                  }}
                >
                  Retry
                </button>
                <button
                  onClick={() => setViewMode('code')}
                  style={{
                    padding: '6px 14px', borderRadius: '6px',
                    background: 'transparent', color: '#fff',
                    border: '1px solid rgba(255,255,255,0.3)',
                    cursor: 'pointer', fontSize: '12px',
                  }}
                >
                  View source
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* BOM mode: component table */}
      {viewMode === 'bom' && (
        <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
          {bomItems.length === 0 ? (
            <div style={{ textAlign: 'center', opacity: 0.5, padding: '40px 0', fontSize: '13px' }}>
              No components found in circuit files.
            </div>
          ) : (
            <>
              <div style={{ fontSize: '11px', opacity: 0.5, marginBottom: '8px' }}>
                {bomItems.length} components — {bomGrouped.length} unique
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(128,128,128,0.3)', textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px', fontWeight: 600, opacity: 0.7 }}>Qty</th>
                    <th style={{ padding: '6px 8px', fontWeight: 600, opacity: 0.7 }}>Type</th>
                    <th style={{ padding: '6px 8px', fontWeight: 600, opacity: 0.7 }}>Value</th>
                    <th style={{ padding: '6px 8px', fontWeight: 600, opacity: 0.7 }}>Footprint</th>
                    <th style={{ padding: '6px 8px', fontWeight: 600, opacity: 0.7 }}>References</th>
                  </tr>
                </thead>
                <tbody>
                  {bomGrouped.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{row.qty}</td>
                      <td style={{ padding: '6px 8px' }}>{row.type}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{row.value || '—'}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '11px' }}>{row.footprint || '—'}</td>
                      <td style={{ padding: '6px 8px', fontSize: '11px', opacity: 0.7 }}>{row.refs.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Code mode: file tabs + code viewer */}
      {viewMode === 'code' && (
        <>
          {/* File tabs */}
          <div style={{
            display: 'flex',
            gap: '2px',
            padding: '4px 8px',
            borderBottom: '1px solid rgba(128,128,128,0.15)',
            overflowX: 'auto',
            flexShrink: 0,
          }}>
            {circuitFiles.map((file, i) => (
              <button
                key={i}
                onClick={() => setActiveFile(i)}
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  background: i === activeFile ? 'rgba(59,130,246,0.2)' : 'transparent',
                  color: i === activeFile ? '#3b82f6' : 'inherit',
                  opacity: i === activeFile ? 1 : 0.6,
                }}
              >
                <FileCode size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                {file.file_name.split('/').pop()}
              </button>
            ))}
          </div>

          {/* File description */}
          {currentFile.description && (
            <div style={{
              padding: '6px 12px',
              fontSize: '11px',
              opacity: 0.5,
              borderBottom: '1px solid rgba(128,128,128,0.1)',
              flexShrink: 0,
            }}>
              {currentFile.description}
            </div>
          )}

          {/* Code viewer */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: '12px',
          }}>
            <pre style={{
              margin: 0,
              fontSize: '12px',
              lineHeight: 1.5,
              fontFamily: "'Fira Code', 'Consolas', monospace",
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}>
              <code>{currentFile.code}</code>
            </pre>
          </div>
        </>
      )}

      {/* Footer with download button for current file */}
      <div style={{
        padding: '8px 12px',
        borderTop: '1px solid rgba(128,128,128,0.2)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {circuitFiles.length > 1 && (
            <>
              <button
                onClick={() => setActiveFile(Math.max(0, activeFile - 1))}
                disabled={activeFile === 0}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: activeFile === 0 ? 0.3 : 0.7, padding: '4px' }}
              >
                <ChevronLeft size={16} />
              </button>
              <span style={{ fontSize: '11px', opacity: 0.5 }}>{activeFile + 1} / {circuitFiles.length}</span>
              <button
                onClick={() => setActiveFile(Math.min(circuitFiles.length - 1, activeFile + 1))}
                disabled={activeFile === circuitFiles.length - 1}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: activeFile === circuitFiles.length - 1 ? 0.3 : 0.7, padding: '4px' }}
              >
                <ChevronRight size={16} />
              </button>
            </>
          )}
        </div>
        {viewMode === 'bom' ? (
          <button
            onClick={() => {
              const header = 'Qty,Type,Value,Footprint,References';
              const rows = bomGrouped.map(r =>
                `${r.qty},"${r.type}","${r.value || ''}","${r.footprint || ''}","${r.refs.join(', ')}"`
              );
              const csv = [header, ...rows].join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${circuitTitle || 'circuit'}-bom.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            disabled={bomItems.length === 0}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 12px', borderRadius: '6px',
              border: '1px solid rgba(59,130,246,0.3)',
              background: 'rgba(59,130,246,0.1)',
              color: '#3b82f6', cursor: 'pointer', fontSize: '12px',
              opacity: bomItems.length === 0 ? 0.4 : 1,
            }}
          >
            <Download size={14} />
            Download BOM (.csv)
          </button>
        ) : (
          <button
            onClick={() => downloadFile(currentFile)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 12px', borderRadius: '6px',
              border: '1px solid rgba(59,130,246,0.3)',
              background: 'rgba(59,130,246,0.1)',
              color: '#3b82f6', cursor: 'pointer', fontSize: '12px',
            }}
          >
            <Download size={14} />
            Download {currentFile.file_name.split('/').pop()}
          </button>
        )}
      </div>
    </div>
  );
};

export default CircuitEditorPanel;
